import React, { useState, useMemo, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ChevronRight,
  Search,
  ArrowLeft,
  Zap,
  BrainCircuit,
  Layers,
  GitBranch,
  ExternalLink,
  Sparkles,
  ArrowRight,
  Rocket,
  Workflow,
  Users,
  BarChart3,
  Settings,
  FileText,
  Plug,
  type LucideIcon,
} from "lucide-react";
import { nodeDocsData, nodeCategories } from "./nodeDocsData";
import { overviewSections, overviewGroups, documentSections, documentGroups } from "./overviewDocsData";
import type { OverviewSection } from "./overviewDocsData";

type ActiveTab = "overview" | "integrations" | "documents";

// ─── Category icon names (from nodeCategories.icon) → lucide components ───
const CATEGORY_ICONS: Record<string, LucideIcon> = {
  zap: Zap,
  "brain-circuit": BrainCircuit,
  layers: Layers,
  "git-branch": GitBranch,
  "external-link": ExternalLink,
};

// Small colored icon per guide group (for the sidebar)
const GROUP_META: Record<string, { icon: LucideIcon; tone: string }> = {
  "Getting Started": { icon: Rocket, tone: "text-amber-500" },
  "Workflow Automation": { icon: Workflow, tone: "text-indigo-500" },
  "User Management": { icon: Users, tone: "text-blue-500" },
  "Reports & Analytics": { icon: BarChart3, tone: "text-emerald-500" },
  "Settings": { icon: Settings, tone: "text-gray-500" },
  "Document Management": { icon: FileText, tone: "text-violet-500" },
  "Import & Connectors": { icon: Plug, tone: "text-sky-500" },
};

// Small colored tone per node category color (for the Workflow sidebar)
const CATEGORY_TONE: Record<string, string> = {
  amber: "text-amber-500",
  violet: "text-violet-500",
  indigo: "text-indigo-500",
  emerald: "text-emerald-500",
};

// Hand-picked popular nodes for the docs home
const POPULAR_NODES = ["FILE-UPLOAD", "FILE-OCR", "SMART-PARSE", "GEMINI-UNDERSTANDING", "SUMMARY", "WEBHOOK"];

const WorkflowNodeDocs: React.FC = () => {
  const { nodeType } = useParams<{ nodeType: string }>();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState<ActiveTab>(nodeType ? "integrations" : "overview");
  const [activeSection, setActiveSection] = useState(overviewSections[0]?.id || "");
  const overviewScrollRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [tocItems, setTocItems] = useState<Array<{ id: string; label: string }>>([]);
  const [activeTocId, setActiveTocId] = useState("doc-overview");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 1200);
    return () => clearTimeout(timer);
  }, []);

  // ⌘K / Ctrl+K → jump to Integrations and focus the search box
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setActiveTab("integrations");
        setTimeout(() => searchInputRef.current?.focus(), 50);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const currentDoc = nodeType ? nodeDocsData[nodeType] : undefined;

  // Guide-style tabs (Using DocxIQ + Documents) share the same rendering,
  // differing only in their section/group source and heading.
  const isGuide = activeTab === "overview" || activeTab === "documents";
  const guideSections = activeTab === "documents" ? documentSections : overviewSections;
  const guideGroups = activeTab === "documents" ? documentGroups : overviewGroups;
  const guideSidebarTitle = activeTab === "documents" ? "Documents" : "Using DocxIQ";
  const guideHeading = activeTab === "documents" ? "Documents" : "DocxIQ Documentation";
  const guideIntro = activeTab === "documents"
    ? "Everything in the Documents area — stores, upload, processing, search, plus the connectors that bring data in (web scraping, spreadsheets, APIs, and databases)."
    : "Complete guide to using the DocxIQ platform — AI processing, workflow automation, users, reports, and settings.";

  const currentCategory = useMemo(() => {
    if (!nodeType) return null;
    return nodeCategories.find((cat) => cat.nodes.includes(nodeType)) || null;
  }, [nodeType]);

  const relatedNodes = useMemo(() => {
    if (!currentCategory || !nodeType) return [];
    return currentCategory.nodes.filter((n) => n !== nodeType);
  }, [currentCategory, nodeType]);

  const filteredCategories = useMemo(() => {
    if (!searchQuery.trim()) return nodeCategories;
    const q = searchQuery.toLowerCase();
    return nodeCategories
      .map((cat) => ({
        ...cat,
        nodes: cat.nodes.filter((key) => {
          const doc = nodeDocsData[key];
          return (
            key.toLowerCase().includes(q) ||
            doc?.title.toLowerCase().includes(q) ||
            doc?.description.toLowerCase().includes(q)
          );
        }),
      }))
      .filter((cat) => cat.nodes.length > 0);
  }, [searchQuery]);

  useEffect(() => {
    const scrollContainer = document.getElementById("docs-inner-viewport");
    if (scrollContainer) {
      scrollContainer.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [nodeType]);

  // Switch to integrations tab when a node is selected via URL
  useEffect(() => {
    if (nodeType) setActiveTab("integrations");
  }, [nodeType]);

  // Intersection observer for guide (overview + documents) section highlighting
  useEffect(() => {
    if ((activeTab !== "overview" && activeTab !== "documents") || !overviewScrollRef.current) return;
    const container = overviewScrollRef.current;
    const sectionEls = container.querySelectorAll("[data-section-id]");
    if (!sectionEls.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            const id = (entry.target as HTMLElement).dataset.sectionId;
            if (id) setActiveSection(id);
          }
        }
      },
      { root: container, rootMargin: "-10% 0px -80% 0px", threshold: 0 }
    );
    sectionEls.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [activeTab]);

  // Extract TOC headings from rendered node doc content
  useEffect(() => {
    if (!currentDoc || activeTab !== "integrations") {
      setTocItems([]);
      return;
    }

    const extractToc = () => {
      const container = document.getElementById("docs-inner-viewport");
      if (!container) return;

      const items: Array<{ id: string; label: string }> = [
        { id: "doc-overview", label: "Overview" },
      ];

      const sections = container.querySelectorAll(".wf-docs-content section");
      sections.forEach((section) => {
        const h2 = section.querySelector("h2");
        if (h2) {
          const text = h2.textContent || "";
          const id = `toc-${text.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/-$/, "")}`;
          section.id = id;
          items.push({ id, label: text });
        }
      });

      const tipCallout = container.querySelector(".wf-docs-content .wf-callout-tip");
      if (tipCallout) {
        tipCallout.id = "toc-tips";
        items.push({ id: "toc-tips", label: "Tips" });
      }

      setTocItems(items);
      setActiveTocId("doc-overview");
    };

    requestAnimationFrame(extractToc);
  }, [nodeType, currentDoc, activeTab]);

  // IntersectionObserver for integrations TOC active tracking
  useEffect(() => {
    if (!tocItems.length || activeTab !== "integrations" || !currentDoc) return;

    const container = document.getElementById("docs-inner-viewport");
    if (!container) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && entry.target.id) {
            setActiveTocId(entry.target.id);
          }
        }
      },
      { root: container, rootMargin: "-5% 0px -85% 0px", threshold: 0 }
    );

    tocItems.forEach(({ id }) => {
      const el = container.querySelector(`#${CSS.escape(id)}`);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [tocItems, activeTab, currentDoc]);

  const flatNodes = useMemo(() => {
    return nodeCategories.flatMap((c) => c.nodes);
  }, []);

  const currentIndex = nodeType ? flatNodes.indexOf(nodeType) : -1;
  const prevNode = currentIndex > 0 ? flatNodes[currentIndex - 1] : null;
  const nextNode =
    currentIndex < flatNodes.length - 1 ? flatNodes[currentIndex + 1] : null;

  if (isLoading) {
    return (
      <div className="h-screen bg-white flex flex-col items-center justify-center relative overflow-hidden">
        {/* Background ambient glow */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="wf-loader-glow wf-loader-glow-1" />
          <div className="wf-loader-glow wf-loader-glow-2" />
        </div>

        {/* Spinner */}
        <div className="relative z-10 flex flex-col items-center gap-8">
          <div className="wf-loader-ring">
            <div className="wf-loader-ring-inner" />
            <div className="wf-loader-ring-dot" />
          </div>

          {/* Brand + label */}
          <div className="flex flex-col items-center gap-2">
            <span className="text-[18px] font-bold text-gray-900 tracking-tight">DocxIQ</span>
            <div className="flex items-center gap-2">
              <span className="wf-loader-pulse-dot" />
              <span className="text-[13px] text-gray-400 font-medium tracking-wide">Loading documentation</span>
            </div>
          </div>
        </div>

        <style>{`
          .wf-loader-glow {
            position: absolute;
            border-radius: 50%;
            filter: blur(80px);
            opacity: 0.4;
            animation: wf-glow-drift 6s ease-in-out infinite;
          }
          .wf-loader-glow-1 {
            width: 300px; height: 300px;
            background: radial-gradient(circle, #60a5fa 0%, transparent 70%);
            top: 30%; left: 35%;
            animation-delay: 0s;
          }
          .wf-loader-glow-2 {
            width: 250px; height: 250px;
            background: radial-gradient(circle, #6ee7b7 0%, transparent 70%);
            top: 45%; left: 55%;
            animation-delay: -3s;
          }
          @keyframes wf-glow-drift {
            0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.3; }
            50% { transform: translate(20px, -15px) scale(1.15); opacity: 0.5; }
          }

          .wf-loader-ring {
            width: 56px; height: 56px;
            position: relative;
            animation: wf-ring-spin 1.8s linear infinite;
          }
          .wf-loader-ring-inner {
            position: absolute; inset: 0;
            border-radius: 50%;
            border: 2.5px solid #f3f4f6;
            border-top-color: #2563eb;
            border-right-color: #2563eb;
          }
          .wf-loader-ring-dot {
            position: absolute;
            width: 8px; height: 8px;
            background: #2563eb;
            border-radius: 50%;
            top: 0; left: 50%;
            transform: translateX(-50%);
            box-shadow: 0 0 12px 2px rgba(99, 102, 241, 0.4);
          }
          @keyframes wf-ring-spin {
            to { transform: rotate(360deg); }
          }

          .wf-loader-pulse-dot {
            width: 6px; height: 6px;
            background: #2563eb;
            border-radius: 50%;
            animation: wf-pulse 1.4s ease-in-out infinite;
          }
          @keyframes wf-pulse {
            0%, 100% { opacity: 0.3; transform: scale(0.8); }
            50% { opacity: 1; transform: scale(1.2); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div className="h-screen bg-white font-sans text-gray-900 overflow-hidden flex flex-col">
      {/* ─── TOP NAVIGATION BAR ─── */}
      <header className="h-[44px] bg-black text-white flex items-center shrink-0 relative z-30">
        <div className="flex items-center gap-6 w-full max-w-[1440px] mx-auto px-10">
          <span className="text-[13px] font-semibold tracking-tight mr-4">DocxIQ</span>
          <button
            onClick={() => { setActiveTab("overview"); setActiveSection(overviewSections[0]?.id || ""); }}
            className={`text-[12px] font-medium cursor-pointer h-[44px] flex items-center transition-colors border-b-2 ${
              activeTab === "overview" ? "text-white border-white" : "text-gray-400 border-transparent hover:text-gray-200"
            }`}
          >
            Using DocxIQ
          </button>
          <button
            onClick={() => { setActiveTab("documents"); setActiveSection(documentSections[0]?.id || ""); }}
            className={`text-[12px] font-medium cursor-pointer h-[44px] flex items-center transition-colors border-b-2 ${
              activeTab === "documents" ? "text-white border-white" : "text-gray-400 border-transparent hover:text-gray-200"
            }`}
          >
            Documents
          </button>
          <button
            onClick={() => setActiveTab("integrations")}
            className={`text-[12px] font-medium cursor-pointer h-[44px] flex items-center transition-colors border-b-2 ${
              activeTab === "integrations" ? "text-white border-white" : "text-gray-400 border-transparent hover:text-gray-200"
            }`}
          >
            Workflow
          </button>
          <div className="ml-auto">
            <span
              onClick={() => navigate("/dashboard")}
              className="text-[12px] font-medium text-gray-400 hover:text-white cursor-pointer transition-colors"
            >
              DocxIQ Home
            </span>
          </div>
        </div>
      </header>

      {/* ─── MAIN CONTENT CONTAINER (CENTERED) ─── */}
      <div className="flex-1 flex justify-center overflow-hidden bg-gray-50/10">
        <div className="w-full max-w-[1440px] flex bg-white border-x border-gray-100 h-full overflow-hidden">

          {/* ─── LEFT SIDEBAR ─── */}
          <aside className="w-[260px] border-r border-stone-100 flex flex-col shrink-0 bg-white">
            {activeTab === "integrations" ? (
              <>
                <div className="p-6 pb-2">
                  <h2 className="text-[13px] font-bold text-gray-900 mb-4">Workflow Nodes</h2>
                  <div className="relative mb-2">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                    <input
                      ref={searchInputRef}
                      type="text"
                      placeholder="Search nodes... (⌘K)"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full bg-stone-50 border border-stone-200 pl-8 pr-3 py-[6px] text-[13px] rounded text-gray-700 focus:outline-none focus:border-stone-300 placeholder-stone-400 transition-all"
                    />
                  </div>
                </div>
                <nav className="flex-1 overflow-y-auto px-4 py-2 wf-docs-scrollbar">
                  {filteredCategories.map((category) => {
                    const CatIcon = CATEGORY_ICONS[(category as any).icon] || Sparkles;
                    const catTone = CATEGORY_TONE[(category as any).color] || "text-gray-400";
                    return (
                    <div key={category.name} className="mb-4">
                      <div className="flex items-center gap-1.5 px-2 mb-2">
                        <CatIcon className={`w-3.5 h-3.5 ${catTone}`} />
                        <span className="text-[11px] font-extrabold text-gray-500 uppercase tracking-wider">
                          {category.name}
                        </span>
                      </div>
                      {category.nodes.map((key) => {
                        const doc = nodeDocsData[key];
                        if (!doc) return null;
                        const isActive = nodeType === key;
                        return (
                          <button
                            key={key}
                            onClick={() => navigate(`/docs/${key}`)}
                            className={`w-full text-left px-2 py-1.5 text-[14px] transition-colors rounded ${
                              isActive
                                ? "text-blue-600 font-semibold bg-stone-50"
                                : "text-gray-600 hover:text-gray-900"
                            }`}
                          >
                            {doc.title}
                          </button>
                        );
                      })}
                    </div>
                    );
                  })}
                </nav>
              </>
            ) : (
              <>
                <div className="p-6 pb-2">
                  <h2 className="text-[13px] font-bold text-gray-900 mb-4">{guideSidebarTitle}</h2>
                </div>
                <nav className="flex-1 overflow-y-auto px-4 py-2 wf-docs-scrollbar">
                  {guideGroups.map((groupName) => {
                    const groupSections = guideSections.filter(
                      (s: OverviewSection) => s.group === groupName
                    );
                    if (groupSections.length === 0) return null;
                    const gMeta = GROUP_META[groupName];
                    const GIcon = gMeta?.icon || Sparkles;
                    return (
                      <div key={groupName} className="mb-4">
                        <div className="flex items-center gap-1.5 px-2 mb-2">
                          <GIcon className={`w-3.5 h-3.5 ${gMeta?.tone || "text-gray-400"}`} />
                          <span className="text-[11px] font-extrabold text-gray-500 uppercase tracking-wider">
                            {groupName}
                          </span>
                        </div>
                        {groupSections.map((section: OverviewSection) => {
                          const isActive = activeSection === section.id;
                          return (
                            <button
                              key={section.id}
                              onClick={() => {
                                setActiveSection(section.id);
                                const el = overviewScrollRef.current?.querySelector(`[data-section-id="${section.id}"]`);
                                el?.scrollIntoView({ behavior: "smooth", block: "start" });
                              }}
                              className={`w-full text-left px-2 py-1.5 text-[14px] transition-colors rounded ${
                                isActive
                                  ? "text-blue-600 font-semibold bg-stone-50"
                                  : "text-gray-600 hover:text-gray-900"
                              }`}
                            >
                              {section.title}
                            </button>
                          );
                        })}
                      </div>
                    );
                  })}
                </nav>
              </>
            )}

            <div className="p-4 border-t border-stone-50">
              <button
                onClick={() => navigate("/workflow")}
                className="flex items-center gap-2 text-[12px] text-gray-400 hover:text-gray-700 transition-colors"
              >
                <ArrowLeft className="w-3 h-3" />
                Back to Workflow
              </button>
            </div>
          </aside>

          {/* ─── MAIN CONTENT PANEL ─── */}
          <main className="flex-1 flex overflow-hidden">
            {isGuide ? (
              /* ── Guide Content (Using DocxIQ / Documents) ── */
              <div ref={overviewScrollRef} className="flex-1 overflow-y-auto px-12 py-10 wf-docs-scrollbar scroll-smooth">
                <div className="max-w-[780px]">
                  <h1 className="text-[28px] font-bold text-gray-900 tracking-tight leading-tight mb-2">
                    {guideHeading}
                  </h1>
                  <p className="text-[14px] text-gray-500 leading-relaxed mb-10">
                    {guideIntro}
                  </p>

                  {guideSections.map((section) => (
                    <section
                      key={section.id}
                      data-section-id={section.id}
                      className="mb-14 scroll-mt-10"
                    >
                      <h2 className="text-[22px] font-bold text-gray-800 tracking-tight mb-4 pb-2 border-b border-stone-100">
                        {section.title}
                      </h2>
                      <div className="wf-docs-content wf-overview-content">
                        <div dangerouslySetInnerHTML={{ __html: section.content }} />
                      </div>
                    </section>
                  ))}

                  <div className="pb-20" />
                </div>
              </div>
            ) : currentDoc ? (
              /* ── Integrations: Node Doc ── */
              <div className="flex-1 flex min-w-0 h-full">
                <div id="docs-inner-viewport" className="flex-1 overflow-y-auto px-12 py-10 wf-docs-scrollbar scroll-smooth">
                  <div className="flex items-center gap-1.5 text-[13px] text-gray-400 mb-4">
                    <span className="cursor-pointer hover:text-gray-600">Workflow</span>
                    <ChevronRight className="w-3.5 h-3.5" />
                    <span className="cursor-pointer hover:text-gray-600">{currentDoc.badge} nodes</span>
                    <ChevronRight className="w-3.5 h-3.5" />
                    <span className="text-gray-800 font-normal">{currentDoc.title}</span>
                  </div>

                  {(() => {
                    const HeroIcon = CATEGORY_ICONS[(currentCategory as any)?.icon] || Sparkles;
                    return (
                      <div className="flex items-center gap-3 mb-5">
                        <div className="w-9 h-9 rounded-lg bg-gray-100 border border-gray-200 flex items-center justify-center text-gray-500 shrink-0">
                          <HeroIcon className="w-[18px] h-[18px]" />
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <span className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 border border-gray-200">
                              {currentDoc.badge}
                            </span>
                            <span className="text-[12px] text-gray-400">{currentDoc.subtitle}</span>
                          </div>
                          <h1 id="doc-overview" className="text-[22px] font-bold text-gray-900 tracking-tight leading-tight scroll-mt-6">
                            {currentDoc.title}
                          </h1>
                        </div>
                      </div>
                    );
                  })()}

                  <div className="mb-9 text-[14px] text-gray-600 leading-relaxed max-w-[720px]">
                    {currentDoc.description}
                  </div>

                  <div className="wf-docs-content">
                    <div dangerouslySetInnerHTML={{ __html: currentDoc.content }} />
                  </div>

                  <footer className="mt-20 pt-10 border-t border-stone-100 flex items-stretch gap-4 pb-20">
                    {prevNode ? (
                      <button
                        onClick={() => navigate(`/docs/${prevNode}`)}
                        className="flex-1 group text-left py-2"
                      >
                        <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Previous</div>
                        <div className="text-[14px] font-medium text-gray-600 group-hover:text-blue-600 transition-colors">
                          ← {nodeDocsData[prevNode]?.title}
                        </div>
                      </button>
                    ) : <div className="flex-1" />}
                    {nextNode ? (
                      <button
                        onClick={() => navigate(`/docs/${nextNode}`)}
                        className="flex-1 group text-right py-2"
                      >
                        <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Next</div>
                        <div className="text-[14px] font-medium text-gray-600 group-hover:text-blue-600 transition-colors">
                          {nodeDocsData[nextNode]?.title} →
                        </div>
                      </button>
                    ) : <div className="flex-1" />}
                  </footer>
                </div>

                {/* ─── Right Sidebar: Dynamic TOC + Related ─── */}
                <aside className="w-[220px] hidden xl:block py-8 overflow-y-auto shrink-0 border-l border-stone-100 wf-docs-scrollbar">
                  {/* On this page */}
                  {tocItems.length > 0 && (
                    <div className="px-5">
                      <h3 className="text-[11px] font-extrabold text-gray-500 uppercase tracking-[0.1em] mb-3">On this page</h3>
                      <nav className="space-y-0.5">
                        {tocItems.map((item) => (
                          <button
                            key={item.id}
                            onClick={() => {
                              const container = document.getElementById("docs-inner-viewport");
                              const el = container?.querySelector(`#${CSS.escape(item.id)}`);
                              if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                              setActiveTocId(item.id);
                            }}
                            className={`block w-full text-left py-1 text-[13px] transition-colors ${
                              activeTocId === item.id
                                ? "text-blue-600 font-semibold"
                                : "text-gray-400 hover:text-gray-700"
                            }`}
                          >
                            {item.label}
                          </button>
                        ))}
                      </nav>
                    </div>
                  )}

                  {/* Related nodes in same category */}
                  {relatedNodes.length > 0 && (
                    <div className="px-5 mt-6 pt-6 border-t border-stone-100">
                      <h3 className="text-[11px] font-extrabold text-gray-500 uppercase tracking-[0.1em] mb-3">
                        {currentCategory?.name || "Related"}
                      </h3>
                      <nav className="space-y-0.5">
                        {relatedNodes.map((key) => {
                          const doc = nodeDocsData[key];
                          if (!doc) return null;
                          return (
                            <button
                              key={key}
                              onClick={() => navigate(`/docs/${key}`)}
                              className="block w-full text-left py-1 text-[13px] text-gray-400 hover:text-blue-600 transition-colors"
                            >
                              {doc.title}
                            </button>
                          );
                        })}
                      </nav>
                    </div>
                  )}
                </aside>
              </div>
            ) : (
              /* ── Integrations: Docs Home ── */
              <div className="flex-1 overflow-y-auto wf-docs-scrollbar">
                <div className="px-8 py-8">
                  {/* Hero */}
                  <div className="mb-10">
                    <h1 className="text-[26px] font-bold text-gray-900 tracking-tight mb-1.5">
                      Workflow nodes
                    </h1>
                    <p className="text-[14px] text-gray-500 leading-relaxed max-w-[560px] mb-5">
                      Every node you can drop on the canvas — what it does, when to use it, and how to configure it.
                    </p>
                    <div className="relative max-w-[460px]">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search nodes…"
                        className="w-full bg-white border border-gray-200 rounded-lg pl-9 pr-14 py-2.5 text-[13px] text-gray-700 focus:outline-none focus:border-gray-300 transition-all"
                      />
                      <kbd className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] font-medium text-gray-400 bg-gray-100 border border-gray-200 rounded px-1.5 py-0.5">⌘K</kbd>
                    </div>

                    {/* Quick links */}
                    <div className="flex items-center gap-2 mt-4">
                      <button
                        onClick={() => { setActiveTab("overview"); setActiveSection(overviewSections[0]?.id || ""); }}
                        className="inline-flex items-center gap-1 text-[12px] font-medium text-gray-500 hover:text-gray-900 bg-white border border-gray-200 rounded-md px-2.5 py-1 transition-colors"
                      >
                        Quick start <ArrowRight className="w-3 h-3" />
                      </button>
                      <button
                        onClick={() => navigate("/workflow")}
                        className="inline-flex items-center gap-1 text-[12px] font-medium text-gray-500 hover:text-gray-900 bg-white border border-gray-200 rounded-md px-2.5 py-1 transition-colors"
                      >
                        Templates <ArrowRight className="w-3 h-3" />
                      </button>
                      <button
                        onClick={() => setActiveTab("overview")}
                        className="inline-flex items-center gap-1 text-[12px] font-medium text-gray-500 hover:text-gray-900 bg-white border border-gray-200 rounded-md px-2.5 py-1 transition-colors"
                      >
                        Core concepts <ArrowRight className="w-3 h-3" />
                      </button>
                    </div>
                  </div>

                  {/* Category cards */}
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 mb-10">
                    {filteredCategories.map((category) => {
                      const CatIcon = CATEGORY_ICONS[(category as any).icon] || Sparkles;
                      return (
                        <div
                          key={category.name}
                          className="bg-white border border-gray-200 rounded-xl p-4 transition-colors hover:border-gray-300"
                        >
                          <div className="flex items-start gap-2.5 mb-3">
                            <div className="w-8 h-8 rounded-lg bg-gray-100 border border-gray-200 flex items-center justify-center text-gray-500 shrink-0">
                              <CatIcon className="w-4 h-4" />
                            </div>
                            <div className="min-w-0">
                              <div className="flex items-center gap-2">
                                <h3 className="font-semibold text-gray-900 text-[13.5px]">{category.name}</h3>
                                <span className="text-[11px] text-gray-400">{category.nodes.length}</span>
                              </div>
                              <p className="text-[12px] text-gray-500 leading-snug mt-0.5">{(category as any).description}</p>
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-1.5">
                            {category.nodes.map((key) => {
                              const doc = nodeDocsData[key];
                              if (!doc) return null;
                              return (
                                <button
                                  key={key}
                                  onClick={() => navigate(`/docs/${key}`)}
                                  className="text-[12px] font-medium px-2 py-0.5 rounded-md border border-gray-200 bg-gray-50 text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors"
                                >
                                  {doc.title}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Popular */}
                  <div>
                    <h3 className="text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-2.5">Popular nodes</h3>
                    <div className="flex flex-wrap gap-2">
                      {POPULAR_NODES.map((key) => {
                        const doc = nodeDocsData[key];
                        if (!doc) return null;
                        return (
                          <button
                            key={key}
                            onClick={() => navigate(`/docs/${key}`)}
                            className="group flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-1.5 hover:border-gray-300 transition-colors"
                          >
                            <span className="w-1.5 h-1.5 rounded-full bg-gray-300" />
                            <span className="text-[12.5px] font-medium text-gray-700">{doc.title}</span>
                            <ArrowRight className="w-3.5 h-3.5 text-gray-300 group-hover:text-gray-500 transition-colors" />
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="pb-16" />
                </div>
              </div>
            )}
          </main>
        </div>
      </div>

      <style>{`
        .wf-docs-scrollbar::-webkit-scrollbar { width: 5px; }
        .wf-docs-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .wf-docs-scrollbar::-webkit-scrollbar-thumb { background: #e5e7eb; border-radius: 10px; }
        .wf-docs-scrollbar::-webkit-scrollbar-thumb:hover { background: #d1d5db; }

        /* ─── Screenshots ─── */

        .wf-docs-content .wf-screenshot,
        .wf-overview-content .wf-screenshot {
          margin: 1rem 0 1.25rem;
          border-radius: 8px;
          overflow: hidden;
          border: 1px solid #e5e7eb;
          background: #f9fafb;
        }

        .wf-docs-content .wf-screenshot img,
        .wf-overview-content .wf-screenshot img {
          width: 100%;
          height: auto;
          display: block;
        }

        /* ─── Typography ─── */

        .wf-docs-content h2 {
          font-size: 1.125rem;
          font-weight: 650;
          color: #111827;
          margin-top: 0;
          margin-bottom: 0.75rem;
          letter-spacing: -0.01em;
        }

        .wf-docs-content > div > section + section { margin-top: 0; }

        .wf-docs-content section[id] {
          scroll-margin-top: 1.5rem;
        }

        .wf-docs-content .wf-callout-tip[id] {
          scroll-margin-top: 1.5rem;
        }

        .wf-docs-content p {
          font-size: 0.875rem;
          line-height: 1.8;
          color: #4b5563;
          margin-bottom: 0.75rem;
        }

        .wf-docs-content p:last-child { margin-bottom: 0; }

        /* ─── Tables ─── */

        .wf-docs-content table {
          width: 100%;
          border-collapse: collapse;
          margin: 0.5rem 0 0;
          font-size: 0.8125rem;
        }

        .wf-docs-content thead th {
          text-align: left;
          padding: 0.5rem 0.875rem;
          font-size: 0.6875rem;
          font-weight: 600;
          color: #9ca3af;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          border-bottom: 1px solid #e5e7eb;
        }

        .wf-docs-content tbody td {
          padding: 0.625rem 0.875rem;
          color: #374151;
          border-bottom: 1px solid #f3f4f6;
          vertical-align: top;
          font-size: 0.8125rem;
          line-height: 1.5;
        }

        .wf-docs-content tbody tr:last-child td { border-bottom: none; }
        .wf-docs-content tbody tr:hover td { background: #fafbfc; }

        /* ─── Inline code ─── */

        .wf-docs-content code {
          background: #f3f4f6;
          color: #1f2937;
          padding: 0.1rem 0.35rem;
          border-radius: 3px;
          font-size: 0.8rem;
          font-weight: 550;
          font-family: 'SF Mono', 'Cascadia Code', 'Fira Code', monospace;
        }

        /* ─── Steps (timeline) ─── */

        .wf-docs-content .wf-steps {
          display: flex;
          flex-direction: column;
          gap: 0;
          margin: 0.75rem 0 0.25rem;
          border-left: 2px solid #e5e7eb;
          margin-left: 11px;
        }

        .wf-docs-content .wf-step {
          display: flex;
          align-items: flex-start;
          gap: 0.875rem;
          padding: 0.75rem 0 0.75rem 1.25rem;
          position: relative;
        }

        .wf-docs-content .wf-step-num {
          width: 22px;
          height: 22px;
          border-radius: 50%;
          background: #f3f4f6;
          color: #6b7280;
          font-size: 0.6875rem;
          font-weight: 700;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          position: absolute;
          left: -13px;
          top: 0.75rem;
          border: 2px solid #fff;
        }

        .wf-docs-content .wf-step-num.wf-step-done {
          background: #111827;
          color: #fff;
        }

        .wf-docs-content .wf-step > div:last-child {
          padding-left: 0.5rem;
        }

        .wf-docs-content .wf-step-title {
          font-size: 0.8125rem;
          font-weight: 600;
          color: #111827;
          margin-bottom: 0.125rem;
          line-height: 1.4;
        }

        .wf-docs-content .wf-step-desc {
          font-size: 0.8125rem;
          color: #6b7280;
          line-height: 1.6;
          margin-bottom: 0;
        }

        /* ─── Callouts ─── */

        .wf-docs-content .wf-callout {
          padding: 0.875rem 1rem;
          border-radius: 6px;
          font-size: 0.8125rem;
          line-height: 1.7;
          color: #374151;
        }

        .wf-docs-content .wf-callout-info {
          background: #f8fafc;
          border-left: 3px solid #3b82f6;
        }

        .wf-docs-content .wf-callout-tip {
          background: #f8fafc;
          border-left: 3px solid #f59e0b;
        }

        .wf-docs-content .wf-callout-label {
          font-size: 0.75rem;
          font-weight: 700;
          color: #92400e;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          margin-bottom: 0.375rem;
        }

        .wf-docs-content .wf-callout ul {
          margin: 0;
          padding-left: 1rem;
          list-style: disc;
        }

        .wf-docs-content .wf-callout li {
          font-size: 0.8125rem;
          color: #4b5563;
          line-height: 1.7;
          margin-bottom: 0.125rem;
        }

        /* ─── Bullet lists ─── */

        .wf-docs-content .wf-list {
          margin: 0.5rem 0 0;
          padding-left: 1.125rem;
          list-style: disc;
        }

        .wf-docs-content .wf-list li {
          font-size: 0.8125rem;
          color: #4b5563;
          line-height: 1.75;
          margin-bottom: 0.375rem;
        }

        .wf-docs-content .wf-list li strong {
          color: #111827;
          font-weight: 600;
        }

        /* ─── Use cases ─── */

        .wf-docs-content .wf-use-cases {
          display: flex;
          flex-direction: column;
          gap: 1rem;
          margin-top: 0.5rem;
        }

        .wf-docs-content .wf-use-case {
          padding: 1rem 1.125rem;
          background: #fafbfc;
          border: 1px solid #f0f1f3;
          border-radius: 6px;
        }

        .wf-docs-content .wf-use-case-title {
          font-size: 0.8125rem;
          font-weight: 650;
          color: #111827;
          margin-bottom: 0.25rem !important;
        }

        .wf-docs-content .wf-use-case-flow {
          font-size: 0.75rem;
          font-family: 'SF Mono', 'Cascadia Code', 'Fira Code', monospace;
          color: #9ca3af;
          margin-bottom: 0.5rem !important;
          letter-spacing: -0.01em;
        }

        .wf-docs-content .wf-use-case p:last-child {
          font-size: 0.8125rem;
          color: #6b7280;
          margin-bottom: 0;
        }

        /* ─── Code blocks ─── */

        .wf-docs-content .wf-code-block {
          margin: 0.75rem 0 0.25rem;
          border-radius: 8px;
          overflow: hidden;
          border: 1px solid #e5e7eb;
        }

        .wf-docs-content .wf-code-header {
          background: #f3f4f6;
          padding: 0.375rem 0.875rem;
          font-size: 0.6875rem;
          font-weight: 600;
          color: #6b7280;
          font-family: 'SF Mono', 'Cascadia Code', 'Fira Code', monospace;
          letter-spacing: 0.02em;
          border-bottom: 1px solid #e5e7eb;
        }

        .wf-docs-content .wf-code-block pre {
          margin: 0;
          padding: 1rem;
          background: #1f2937;
          overflow-x: auto;
        }

        .wf-docs-content .wf-code-block pre code {
          background: none;
          padding: 0;
          border-radius: 0;
          color: #d1d5db;
          font-size: 0.8rem;
          line-height: 1.7;
          font-family: 'SF Mono', 'Cascadia Code', 'Fira Code', monospace;
          white-space: pre;
        }

        /* ─── HTTP method badges ─── */

        .wf-docs-content .wf-method-list {
          display: flex;
          gap: 0.375rem;
          flex-wrap: wrap;
          margin-top: 0.25rem;
        }

        .wf-docs-content .wf-method-list code {
          font-size: 0.6875rem;
          font-weight: 700;
          padding: 0.25rem 0.625rem;
          border-radius: 4px;
          background: #f3f4f6;
          color: #374151;
          letter-spacing: 0.02em;
        }

        /* ─── Legacy class overrides (for other doc files still using old format) ─── */

        .wf-docs-content .bg-blue-50\\/70,
        .wf-docs-content [class*="bg-blue-50"] {
          background: #f8fafc !important;
          border: none !important;
          border-left: 3px solid #3b82f6 !important;
          border-radius: 0 6px 6px 0 !important;
          padding: 0.875rem 1rem !important;
          margin: 0 !important;
        }

        .wf-docs-content .bg-amber-50\\/70,
        .wf-docs-content [class*="bg-amber-50"] {
          background: #f8fafc !important;
          border: none !important;
          border-left: 3px solid #f59e0b !important;
          border-radius: 0 6px 6px 0 !important;
          padding: 0.875rem 1rem !important;
          margin: 0 !important;
        }

        .wf-docs-content section .grid,
        .wf-docs-content section .flex.items-stretch {
          gap: 0.75rem !important;
          margin-top: 0.75rem;
        }

        .wf-docs-content section .grid > div,
        .wf-docs-content section .flex.items-stretch > div.flex-1 {
          background: #fafbfc !important;
          border: 1px solid #f0f1f3 !important;
          border-radius: 6px !important;
          padding: 1rem !important;
          box-shadow: none !important;
        }

        .wf-docs-content .flex.items-center.shrink-0.text-gray-300 { display: none; }

        .wf-docs-content .rounded-lg.border {
          border-color: #f0f1f3 !important;
          box-shadow: none !important;
          border-radius: 6px !important;
        }

        .wf-docs-content .bg-gray-50 { background-color: #fafbfc !important; }

        .wf-docs-content .text-blue-600.font-bold,
        .wf-docs-content [class*="text-"][class*="font-bold"]:has(+ p) {
          font-size: 0.625rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          color: #9ca3af;
          display: block;
          margin-bottom: 0.125rem;
        }

        .wf-docs-content span[class*="bg-"][class*="rounded-full"] {
          width: 6px !important;
          height: 6px !important;
        }

        /* ═══════════════════════════════════════════
           Overview-specific styles
           ═══════════════════════════════════════════ */

        .wf-overview-content h3 {
          font-size: 0.9375rem;
          font-weight: 650;
          color: #1f2937;
          margin-top: 1.75rem;
          margin-bottom: 0.5rem;
        }

        /* ─── Architecture layers ─── */

        .wf-docs-content .wf-arch-layers {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0;
          margin: 1rem 0 1.5rem;
        }

        .wf-docs-content .wf-arch-layer {
          width: 100%;
          padding: 1rem 1.25rem;
          border-radius: 6px;
          border: 1px solid #e5e7eb;
        }

        .wf-docs-content .wf-arch-presentation { background: #fefce8; border-color: #fde68a; }
        .wf-docs-content .wf-arch-engine { background: #eff6ff; border-color: #bfdbfe; }
        .wf-docs-content .wf-arch-integration { background: #f0fdf4; border-color: #bbf7d0; }

        .wf-docs-content .wf-arch-layer-label {
          font-size: 0.6875rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          color: #6b7280;
          margin-bottom: 0.5rem;
        }

        .wf-docs-content .wf-arch-layer-items {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
        }

        .wf-docs-content .wf-arch-layer-items span {
          font-size: 0.8125rem;
          font-weight: 500;
          color: #374151;
          background: rgba(255,255,255,0.7);
          padding: 0.25rem 0.75rem;
          border-radius: 4px;
          border: 1px solid rgba(0,0,0,0.06);
        }

        .wf-docs-content .wf-arch-connector {
          font-size: 1rem;
          color: #9ca3af;
          padding: 0.25rem 0;
          text-align: center;
        }

        /* ─── Example split (user vs engine) ─── */

        .wf-docs-content .wf-example-split {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1rem;
          margin: 1rem 0 1.5rem;
        }

        @media (max-width: 768px) {
          .wf-docs-content .wf-example-split {
            grid-template-columns: 1fr;
          }
        }

        .wf-docs-content .wf-example-block {
          padding: 1.25rem;
          border-radius: 6px;
          border: 1px solid #e5e7eb;
        }

        .wf-docs-content .wf-example-user { background: #fefce8; border-color: #fde68a; }
        .wf-docs-content .wf-example-engine { background: #f0f9ff; border-color: #bae6fd; }

        .wf-docs-content .wf-example-label {
          font-size: 0.6875rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          color: #6b7280;
          margin-bottom: 0.75rem;
        }

        .wf-docs-content .wf-example-flow {
          display: flex;
          align-items: center;
          gap: 0.375rem;
          flex-wrap: wrap;
          margin-bottom: 0.75rem;
        }

        .wf-docs-content .wf-example-node {
          font-size: 0.75rem;
          font-weight: 600;
          color: #374151;
          background: rgba(255,255,255,0.8);
          padding: 0.3rem 0.625rem;
          border-radius: 4px;
          border: 1px solid rgba(0,0,0,0.08);
          white-space: nowrap;
        }

        .wf-docs-content .wf-example-arrow {
          font-size: 0.875rem;
          color: #9ca3af;
        }

        .wf-docs-content .wf-example-pipeline {
          display: flex;
          flex-wrap: wrap;
          gap: 0.375rem;
          margin-bottom: 0.75rem;
        }

        .wf-docs-content .wf-example-pipeline span {
          font-size: 0.6875rem;
          font-weight: 600;
          color: #1e40af;
          background: rgba(255,255,255,0.8);
          padding: 0.25rem 0.5rem;
          border-radius: 4px;
          border: 1px solid rgba(59,130,246,0.2);
        }

        .wf-docs-content .wf-example-caption {
          font-size: 0.75rem !important;
          color: #9ca3af !important;
          font-style: italic;
          margin-bottom: 0 !important;
        }

        /* ─── Execution patterns ─── */

        .wf-docs-content .wf-patterns {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
          margin: 0.75rem 0 0.5rem;
        }

        .wf-docs-content .wf-pattern {
          padding: 1rem 1.125rem;
          background: #fafbfc;
          border: 1px solid #f0f1f3;
          border-radius: 6px;
        }

        .wf-docs-content .wf-pattern-label {
          font-size: 0.6875rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          color: #6b7280;
          margin-bottom: 0.5rem;
        }

        .wf-docs-content .wf-pattern-flow {
          display: flex;
          align-items: center;
          gap: 0.375rem;
          margin-bottom: 0.5rem;
          font-size: 0.875rem;
          font-weight: 600;
          color: #374151;
          font-family: 'SF Mono', 'Cascadia Code', 'Fira Code', monospace;
        }

        .wf-docs-content .wf-pattern-flow span {
          background: #f3f4f6;
          padding: 0.2rem 0.5rem;
          border-radius: 3px;
          font-size: 0.8125rem;
        }

        .wf-docs-content .wf-pattern-arrow {
          color: #9ca3af !important;
          background: none !important;
          padding: 0 !important;
        }

        .wf-docs-content .wf-pattern p {
          margin-bottom: 0;
        }

        /* ─── Node categories ─── */

        .wf-docs-content .wf-node-cats {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
          margin: 0.75rem 0 0.5rem;
        }

        .wf-docs-content .wf-node-cat {
          padding: 1rem 1.25rem;
          background: #fafbfc;
          border: 1px solid #f0f1f3;
          border-radius: 6px;
        }

        .wf-docs-content .wf-node-cat-header {
          font-size: 0.8125rem;
          font-weight: 700;
          color: #111827;
          margin-bottom: 0.375rem;
          padding-left: 0.75rem;
          border-left: 3px solid #e5e7eb;
        }

        .wf-docs-content .wf-node-cat p {
          font-size: 0.8125rem;
          color: #6b7280;
          margin-bottom: 0.375rem;
        }

        .wf-docs-content .wf-node-cat .wf-list {
          margin-top: 0.25rem;
        }

        /* ─── Pipeline visual (end-to-end) ─── */

        .wf-docs-content .wf-pipeline-visual {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0;
          margin: 1rem 0 1.5rem;
          padding: 1.5rem;
          background: #fafbfc;
          border: 1px solid #f0f1f3;
          border-radius: 8px;
        }

        .wf-docs-content .wf-pipeline-node {
          font-size: 0.8125rem;
          font-weight: 600;
          color: #374151;
          padding: 0.5rem 1.25rem;
          border-radius: 6px;
          border: 1px solid #e5e7eb;
          background: white;
          text-align: center;
          min-width: 180px;
        }

        .wf-docs-content .wf-pipeline-trigger {
          border-color: #fca5a5;
          background: #fef2f2;
          color: #991b1b;
        }

        .wf-docs-content .wf-pipeline-action {
          border-color: #e5e7eb;
          background: white;
        }

        .wf-docs-content .wf-pipeline-condition {
          border-color: #c4b5fd;
          background: #f5f3ff;
          color: #5b21b6;
        }

        .wf-docs-content .wf-pipeline-export {
          border-color: #6ee7b7;
          background: #ecfdf5;
          color: #065f46;
        }

        .wf-docs-content .wf-pipeline-arrow {
          font-size: 0.875rem;
          color: #9ca3af;
          padding: 0.125rem 0;
        }

        .wf-docs-content .wf-pipeline-branch {
          display: flex;
          gap: 2rem;
          margin: 0.25rem 0;
          padding: 0.75rem 1rem;
          position: relative;
        }

        .wf-docs-content .wf-pipeline-branch::before {
          content: '';
          position: absolute;
          top: 0;
          left: 50%;
          width: 1px;
          height: 100%;
          background: #e5e7eb;
          display: none;
        }

        .wf-docs-content .wf-pipeline-branch-yes,
        .wf-docs-content .wf-pipeline-branch-no {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0;
          flex: 1;
        }

        .wf-docs-content .wf-pipeline-branch-label {
          font-size: 0.6875rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          color: #9ca3af;
          margin-bottom: 0.5rem;
          background: #f3f4f6;
          padding: 0.125rem 0.5rem;
          border-radius: 3px;
        }
      `}</style>
    </div>
  );
};

export default WorkflowNodeDocs;
